import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import type { Message } from "@redotech/redo-agents-common/llm-messages";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  ReturnFlow,
  returnFlowJsonFormat,
} from "@redotech/redo-return-core-common/return-flow";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { toast } from "@redotech/redo-web/alert";
import { useState } from "react";

export function useFlowAgentChat({
  flow,
  flowType,
  onFlowChange,
}: {
  flow?: ReturnFlow;
  flowType: FlowType;
  onFlowChange: (flow: ReturnFlow) => void;
}) {
  const { t } = useTranslation();
  const [showChat, setShowChat] = useState(false);
  const [threadId, setThreadId] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  const team = useRequiredContext(TeamContext);
  const flowAgentEnabled = localStorage.getItem("flowAgentEnabled") === "true";
  const showChatButton = flowType === FlowType.RETURN && flowAgentEnabled;
  const client = useRequiredContext(RedoMerchantRpcClientContext);

  const handleSendMessageToAI = useHandler(
    async ({
      messages,
      newMessages,
    }: {
      messages: Message[];
      newMessages: Message[];
    }) => {
      if (!team || !flow) {
        throw new Error(t("No team context available"));
      }

      // Use newMessages if provided (for new messages), otherwise fall back to messages (for initial load)
      const messagesToProcess =
        newMessages?.length > 0 ? newMessages : messages;
      const lastUserMessage = messagesToProcess.findLast(
        (m) => m.role === "user",
      );
      if (!lastUserMessage || typeof lastUserMessage.content !== "string") {
        throw new Error(t("No user message or flow context available"));
      }

      setLoading(true);
      try {
        const existingFlow = returnFlowJsonFormat.write(flow);

        const iterator = client.generateFlowAgentResponse(
          threadId
            ? {
                type: "continue",
                threadId,
                moreMessages: [lastUserMessage],
                existingFlow: existingFlow as {
                  steps: ReturnFlow["steps"];
                  type: FlowType;
                },
              }
            : {
                type: "new",
                initialMessages: [lastUserMessage],
                teamId: team._id,
                existingFlow: existingFlow as {
                  steps: ReturnFlow["steps"];
                  type: FlowType;
                },
              },
        );

        let flowUpdated = false;
        let lastAssistantMessage: Message | undefined;

        for await (const result of iterator) {
          if (result.threadId) {
            setThreadId(result.threadId);
          }

          // Store messages from the result to get the last assistant message
          if (result.messages && result.messages.length > 0) {
            const assistantMessage = result.messages.findLast(
              (m) => m.role === "assistant",
            );
            if (assistantMessage) {
              lastAssistantMessage = assistantMessage;
            }
          }

          if (result.isUpdatingFlow) {
            flowUpdated = true;
          }

          if (
            result.flow &&
            result.flow.steps &&
            result.flow.steps.length > 0
          ) {
            try {
              const model = returnFlowJsonFormat.read(result.flow);
              onFlowChange(model);
              flowUpdated = true;
            } catch (error) {
              console.warn("Failed to parse flow model:", error);
            }
          }

          if (!result.isUpdatingFlow) {
            break;
          }
        }

        if (flowUpdated) {
          toast(t("Flow updated successfully!"), { variant: "success" });
        }

        return { messages: lastAssistantMessage ? [lastAssistantMessage] : [] };
      } catch (error) {
        console.error("Failed to get AI response:", error);
        const errorMessage =
          error instanceof Error ? error.message : t("Unknown error occurred");

        toast(
          t("Failed to process request: {{errorMessage}}", { errorMessage }),
          { variant: "error" },
        );

        return {
          messages: [
            {
              id: Date.now().toString(),
              role: "assistant" as const,
              content: t(
                "Sorry, I encountered an error: {{errorMessage}}. Please try again.",
                { errorMessage },
              ),
            },
          ],
        };
      } finally {
        setLoading(false);
      }
    },
  );

  return {
    conversationId: `${team?._id}-flow-chat`,
    showChat,
    setShowChat,
    showChatButton,
    handleSendMessageToAI,
    loading,
  };
}
